import CameraAltIcon from '@mui/icons-material/CameraAlt'
import CloseIcon from '@mui/icons-material/Close'
import {
  Alert,
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography
} from '@mui/material'
import { Html5Qrcode } from 'html5-qrcode'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useThemeMode } from '../../../theme'

const SCANNER_ELEMENT_ID = 'snacks-qr-scanner-element'
const SNACKS_TABLE_PATH_RE = /^\/voting\/snacks\/(\d+)$/

const makeStyles = (palette) => ({
  dialog: {
    '& .MuiDialog-paper': {
      margin: 0,
      width: '100%',
      maxWidth: '100%',
      height: '100%',
      maxHeight: '100%',
      borderRadius: 0,
      background: palette.background
    }
  },
  dialogTitle: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    background: palette.headerColor,
    color: palette.paperColor,
    py: 1,
    px: 2
  },
  titleContent: {
    display: 'flex',
    alignItems: 'center',
    gap: 1
  },
  dialogContent: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    p: 2,
    height: '100%'
  },
  scannerContainer: {
    width: '100%',
    maxWidth: '360px',
    minHeight: '260px',
    borderRadius: '8px',
    overflow: 'hidden',
    border: `2px solid ${palette.headerColor}`
  },
  hint: {
    mt: 2,
    color: palette.textColor,
    opacity: 0.7,
    textAlign: 'center'
  },
  errorAlert: {
    mt: 2,
    width: '100%',
    maxWidth: '360px'
  },
  deniedBox: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 2,
    p: 3,
    textAlign: 'center'
  },
  deniedIcon: {
    fontSize: '48px',
    color: palette.secondaryMain
  }
})

export const QRScanner = ({ open, onClose }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { palette } = useThemeMode()
  const sx = makeStyles(palette)

  const [status, setStatus] = useState('idle')
  const [invalidQr, setInvalidQr] = useState(false)
  const scannerRef = useRef(null)

  const stopScanner = useCallback(async () => {
    const scanner = scannerRef.current
    if (!scanner) return
    try {
      const state = scanner.getState()
      // State 2 = SCANNING, state 3 = PAUSED
      if (state === 2 || state === 3) {
        await scanner.stop()
      }
    } catch {
      // ignore stop errors
    }
    try {
      scanner.clear()
    } catch {
      // ignore clear errors
    }
    scannerRef.current = null
  }, [])

  const handleScanSuccess = useCallback(
    (decodedText) => {
      setInvalidQr(false)
      let pathname = ''
      try {
        const url = new URL(decodedText)
        pathname = url.pathname
      } catch {
        // not a valid URL — treat as invalid
        setInvalidQr(true)
        return
      }

      const match = pathname.match(SNACKS_TABLE_PATH_RE)
      if (match) {
        stopScanner().then(() => {
          onClose()
          navigate(`/voting/snacks/${match[1]}`)
        })
      } else {
        setInvalidQr(true)
      }
    },
    [navigate, onClose, stopScanner]
  )

  useEffect(() => {
    if (!open) {
      stopScanner()
      setStatus('idle')
      setInvalidQr(false)
      return
    }

    let cancelled = false

    const startScanner = async () => {
      // Explicitly request camera via getUserMedia first — standalone PWA mode
      // does not always surface the permission prompt through html5-qrcode alone.
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } })
        stream.getTracks().forEach((t) => t.stop())
      } catch {
        if (!cancelled) setStatus('denied')
        return
      }

      if (cancelled) return

      try {
        const scanner = new Html5Qrcode(SCANNER_ELEMENT_ID)
        scannerRef.current = scanner

        await scanner.start(
          { facingMode: 'environment' },
          { fps: 10, qrbox: { width: 250, height: 250 } },
          handleScanSuccess,
          () => {
            // scan failure callback — ignore per-frame failures
          }
        )
        if (!cancelled) setStatus('scanning')
      } catch {
        if (cancelled) return
        setStatus('denied')
      }
    }

    setStatus('starting')
    startScanner()

    return () => {
      cancelled = true
      stopScanner()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const handleClose = useCallback(() => {
    stopScanner().then(() => onClose())
  }, [onClose, stopScanner])

  return (
    <Dialog open={open} onClose={handleClose} fullScreen sx={sx.dialog}>
      <DialogTitle sx={sx.dialogTitle}>
        <Box sx={sx.titleContent}>
          <CameraAltIcon fontSize="small" />
          <Typography variant="subtitle1" fontWeight={600}>
            {t('snacks-qr-scan-title')}
          </Typography>
        </Box>
        <IconButton
          onClick={handleClose}
          size="small"
          aria-label={t('snacks-qr-scan-close')}
          sx={{ color: palette.paperColor }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={sx.dialogContent}>
        {status === 'denied'
          ? (
            <Box sx={sx.deniedBox}>
              <CameraAltIcon sx={sx.deniedIcon} />
              <Typography variant="body1" fontWeight={600}>
                {t('snacks-qr-scan-denied-title')}
              </Typography>
              <Typography variant="body2" sx={{ color: palette.textColor, opacity: 0.7 }}>
                {t('snacks-qr-scan-denied-help')}
              </Typography>
            </Box>
            )
          : (
          <>
            <Box sx={sx.scannerContainer}>
              {status === 'starting' && (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '260px' }}>
                  <CircularProgress sx={{ color: palette.headerColor }} />
                </Box>
              )}
              <div id={SCANNER_ELEMENT_ID} />
            </Box>

            {status === 'scanning' && (
              <Typography variant="body2" sx={sx.hint}>
                {t('snacks-qr-scan-hint')}
              </Typography>
            )}

            {invalidQr && (
              <Alert severity="error" sx={sx.errorAlert}>
                {t('snacks-qr-scan-invalid')}
              </Alert>
            )}
          </>
            )}
      </DialogContent>
    </Dialog>
  )
}
